import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	Text,
	Button,
	Table,
	Modal,
	TextInput,
	Group,
} from '@mantine/core'
import { getOrgExperiences } from '../services/actions/orgActions'
import { createExperience } from '../services/actions/experienceActions'

const ManageExperiencesPage = () => {
	const navigate = useNavigate()
	const orgId = useSelector((state) => state.user.currentOrg?.orgId)
	const orgSlug = useSelector((state) => state.user.currentOrg?.orgSlug)

	const [experiences, setExperiences] = useState([])
	const [error, setError] = useState('')
	const [showModal, setShowModal] = useState(false)
	const [newExperience, setNewExperience] = useState({ name: '', slug: '' })

	const generateSlug = (value) =>
		value
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, '') // remove special characters
			.trim()
			.replace(/\s+/g, '-')

	useEffect(() => {
		if (!orgSlug) return

		const loadExperiences = async () => {
			try {
				const data = await getOrgExperiences(orgSlug)
				setExperiences(data)
			} catch (err) {
				console.error('Failed to load org experiences:', err)
				setError('Failed to load organization experiences.')
			}
		}

		loadExperiences()
	}, [orgSlug])

	const handleCreateExperience = async () => {
		try {
			await createExperience({
				orgId,
				...newExperience,
			})

			setShowModal(false)
			setNewExperience({ name: '', slug: '' })

			const updated = await getOrgExperiences(orgSlug)
			setExperiences(updated)
		} catch (err) {
			console.error('Failed to create experience:', err)
			setError('Failed to create experience.')
		}
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Organization Experiences
			</Title>

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			<Button mb='lg' onClick={() => setShowModal(true)}>
				➕ Create New Experience
			</Button>

			{experiences.length > 0 ? (
				<Table withBorder striped highlightOnHover>
					<thead>
						<tr>
							<th>Name</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{experiences.map((exp) => (
							<tr key={exp.id}>
								<td>{exp.name}</td>
								<td style={{ textAlign: 'right' }}>
									<Button
										size='xs'
										onClick={() => navigate(`/experiences/${exp.slug}`)}
									>
										View
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			) : (
				<Text>No experiences found.</Text>
			)}

			<Modal
				opened={showModal}
				onClose={() => setShowModal(false)}
				title='Create New Experience'
				centered
			>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						handleCreateExperience()
					}}
				>
					<TextInput
						label='Experience Name'
						value={newExperience.name}
						onChange={(e) => {
							const name = e.target.value
							setNewExperience({
								name,
								slug: generateSlug(name),
							})
						}}
						required
						mb='sm'
					/>

					<TextInput
						label='Slug'
						value={newExperience.slug}
						onChange={(e) =>
							setNewExperience({
								...newExperience,
								slug: generateSlug(e.target.value),
							})
						}
						required
					/>

					<Group mt='lg' position='right'>
						<Button variant='light' onClick={() => setShowModal(false)}>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</Group>
				</form>
			</Modal>
		</Box>
	)
}

export default ManageExperiencesPage
